import { useChatStore } from "@lib/stores/chatStore";
import type { ChatThread } from "@lib/types";
import { Loader2, MessageSquare } from "lucide-react";

function statusBadge(thread: ChatThread) {
  const classes: Record<string, string> = {
    active: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    error: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    interrupted: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  };

  if (thread.status !== "active" && thread.status !== "error" && thread.status !== "interrupted") {
    return null;
  }

  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${classes[thread.status] || ""}`}>
      {thread.status}
    </span>
  );
}

type Bucket = {
  label: string;
  threads: ChatThread[];
};

function bucketKey(iso: string | null): string {
  if (!iso) return "older";
  const date = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const ageMs =
    startOfToday - new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const ageDays = Math.floor(ageMs / 86_400_000);

  if (ageDays <= 0) return "today";
  if (ageDays === 1) return "yesterday";
  if (ageDays <= 7) return "previous_7";
  if (ageDays <= 30) return "previous_30";
  return "older";
}

const BUCKET_ORDER: Array<{ key: string; label: string }> = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "previous_7", label: "Previous 7 days" },
  { key: "previous_30", label: "Previous 30 days" },
  { key: "older", label: "Older" },
];

function bucketize(threads: ChatThread[]): Bucket[] {
  const map = new Map<string, ChatThread[]>();
  for (const thread of threads) {
    const key = bucketKey(thread.last_message_at);
    const existing = map.get(key);
    if (existing) {
      existing.push(thread);
    } else {
      map.set(key, [thread]);
    }
  }
  return BUCKET_ORDER.filter((b) => map.has(b.key)).map((b) => ({
    label: b.label,
    threads: map.get(b.key) ?? [],
  }));
}

export function ThreadList() {
  const threads = useChatStore((s) => s.threads);
  const isLoading = useChatStore((s) => s.isLoading);
  const setCurrentThread = useChatStore((s) => s.setCurrentThread);
  const startNewChat = useChatStore((s) => s.startNewChat);

  if (isLoading && threads.length === 0) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 size={20} className="animate-spin text-text-tertiary" />
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12 text-text-tertiary text-sm">
        <MessageSquare size={24} className="mb-2" />
        <p>No past conversations</p>
        <button
          type="button"
          onClick={startNewChat}
          className="text-xs mt-2 underline hover:text-text-primary"
        >
          Start a new chat
        </button>
      </div>
    );
  }

  const buckets = bucketize(threads);

  return (
    <div className="p-2">
      {buckets.map((bucket) => (
        <div key={bucket.label} className="mb-3 last:mb-0">
          <h4 className="px-3 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-text-tertiary">
            {bucket.label}
          </h4>
          <div className="space-y-0.5">
            {bucket.threads.map((thread) => (
              <button
                key={thread.id}
                type="button"
                onClick={() => setCurrentThread(thread.id)}
                className="w-full text-left rounded-md px-3 py-2 hover:bg-surface-secondary transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm text-text-primary truncate flex-1">{thread.title}</span>
                  {statusBadge(thread)}
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
